import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import { useChatDraft } from './useChatDraft';

interface SendChatResponse {
  conversation_id: number;
  chain_id: number;
  task_id: string;
  status: string;
  processing_status: string;
}

interface UseProposalChatOptions {
  orgId: number;
  proposalId: number;
  /** Called when conversation_id is set for the first time */
  onConversationCreated?: (conversationId: number) => void;
  /** Live Celery task_id derived from this conversation's server chain data,
   *  or null when nothing is in flight. Seeds polling on mount/rehydration. */
  resumeTaskId?: string | null;
  /** Conversation id the resumed task belongs to. */
  resumeConversationId?: number | null;
}

/**
 * State machine for proposal chat:
 *
 * ```
 * IDLE ──[user types]──> IDLE (message updated)
 *   │
 *   └──[send clicked]──> SENDING
 *                          │
 *                   [API success]──> PROCESSING
 *                          │              │
 *                   [API error]───> ERROR │
 *                          │              │
 *                          │       [polling complete]
 *                          │              │
 *                          │              v
 *                          └──────────> IDLE
 *                                 (optimisticMessage cleared)
 * ```
 *
 * State ownership:
 * - useProposalChat: message, optimisticMessage, isSending, error, taskId, conversationId
 * - useProposalPolling: isPolling (external, triggered by taskId)
 * - ProposalDetailPanel: isProcessing = isSending || isPolling
 */
export function useProposalChat(options: UseProposalChatOptions) {
  const {
    orgId,
    proposalId,
    onConversationCreated,
    resumeTaskId = null,
    resumeConversationId = null,
  } = options;

  // Message input state
  const [message, setMessage] = useState('');
  const [optimisticMessage, setOptimisticMessage] = useState<string | null>(
    null
  );

  // Draft persistence (composable sub-hook)
  const { hasDraft, clearDraft } = useChatDraft(
    proposalId,
    message,
    setMessage
  );

  // Polling state (set by this hook, consumed by useProposalPolling in parent)
  const [taskId, setTaskId] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<number | null>(null);

  // The task_id this instance has already established (seeded OR sent) for the
  // current proposal. Prevents re-seeding a task we already finished, while
  // still allowing a genuinely newer in-flight task to seed.
  const seededOrSentTaskIdRef = useRef<string | null>(null);

  // The proposal a send was fired for, captured at mutate time. Guards the
  // mutation callbacks against a proposal switch while the request is in
  // flight: a send for proposal A must not apply its task_id — or restore its
  // message — onto proposal B after the panel is reused for B (ProposalSidebar
  // has no remount key). Without this, B would poll/track A's task.
  const sendProposalIdRef = useRef(proposalId);

  const [prevProposalId, setPrevProposalId] = useState(proposalId);
  if (proposalId !== prevProposalId) {
    // Panel reused across proposals without a remount (ProposalSidebar has no
    // key). Drop the previous proposal's polling state and allow a fresh seed.
    setPrevProposalId(proposalId);
    setTaskId(null);
    setConversationId(null);
    setOptimisticMessage(null);
    seededOrSentTaskIdRef.current = null;
  }

  // Send mutation using React Query
  const sendMutation = useMutation({
    mutationFn: async (msg: string) => {
      return apiPost<SendChatResponse>(
        `/api/orgs/${orgId}/proposals/${proposalId}/discuss/`,
        { message: msg }
      );
    },
    onMutate: msg => {
      // Capture the proposal this send belongs to (onMutate runs synchronously
      // at mutate() time, before any proposal switch can re-render).
      sendProposalIdRef.current = proposalId;
      // Optimistic update: show message immediately, clear input
      setOptimisticMessage(msg);
      setMessage(''); // Clear input immediately for better UX
    },
    onSuccess: response => {
      // Drop the result if the panel switched proposals mid-flight — applying
      // this task_id would make the now-current proposal track a stale task.
      if (sendProposalIdRef.current !== proposalId) {
        return;
      }
      logger.debug('[useProposalChat] Send succeeded', {
        taskId: response.task_id,
        conversationId: response.conversation_id,
      });
      setTaskId(response.task_id);
      setConversationId(response.conversation_id);
      seededOrSentTaskIdRef.current = response.task_id;
      clearDraft();
      // Notify parent if this is the first conversation
      if (
        response.conversation_id &&
        !conversationId &&
        onConversationCreated
      ) {
        onConversationCreated(response.conversation_id);
      }
    },
    onError: (error, msg) => {
      logger.error(
        'Failed to send chat message',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'proposals', location: 'useProposalChat' }
      );
      // Don't restore the message onto a different proposal if the panel
      // switched while this send was in flight.
      if (sendProposalIdRef.current !== proposalId) {
        return;
      }
      // Restore message on error so user can retry
      setMessage(msg);
      setOptimisticMessage(null);
    },
  });

  // Seed polling from server-derived chain state. Runs when the derived live
  // task differs from the one we already established — so returning to a
  // finished chain (resumeTaskId falls back to null once the refetch lands)
  // does not loop, and a newer in-flight chain is still picked up.
  useEffect(() => {
    if (taskId || sendMutation.isPending) {
      return;
    }
    if (
      resumeTaskId &&
      resumeConversationId != null &&
      resumeTaskId !== seededOrSentTaskIdRef.current
    ) {
      seededOrSentTaskIdRef.current = resumeTaskId;
      setTaskId(resumeTaskId);
      setConversationId(resumeConversationId);
    }
  }, [resumeTaskId, resumeConversationId, taskId, sendMutation.isPending]);

  // Send message handler (validates before sending)
  const sendMessage = useCallback(
    (msg: string) => {
      const trimmed = msg.trim();
      if (!trimmed || sendMutation.isPending) {
        return;
      }
      sendMutation.mutate(trimmed);
    },
    [sendMutation]
  );

  // Retry last failed message
  const retryLastMessage = useCallback(() => {
    if (sendMutation.variables && sendMutation.isError) {
      sendMutation.mutate(sendMutation.variables);
    }
  }, [sendMutation]);

  // Clear polling state (called by parent when polling completes)
  const clearPolling = useCallback(() => {
    setTaskId(null);
    setConversationId(null);
    setOptimisticMessage(null);
  }, []);

  return {
    // Message state
    message,
    setMessage,
    optimisticMessage,

    // Send state
    isSending: sendMutation.isPending,
    error: sendMutation.error,
    sendMessage,
    retryLastMessage,
    clearError: sendMutation.reset,

    // Polling state (for useProposalPolling integration)
    taskId,
    conversationId,
    clearPolling,

    // Draft state
    hasDraft,
    clearDraft,
  };
}
